import * as React from "react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * US-dollar money field: a leading `$` affix plus live thousands separators
 * ("387,900"), reporting a plain number to the parent.
 *
 * Why this isn't `<NumberInput>` with a `$` glued on: `type="number"` rejects
 * commas outright, so grouped digits can never be shown while typing. The cost
 * of using a text input instead is that every keystroke has to be sanitized
 * here — digits, at most one dot, at most two decimals, never negative. A
 * typed minus sign is dropped rather than clamped, so pasting "-500" yields
 * 500; a negative price is nonsense either way and keeping the magnitude is
 * less surprising than silently zeroing the field.
 *
 * Shares `NumberInput`'s buffer strategy: the displayed text is local state and
 * is never overwritten by the prop while the field is focused, so a half-typed
 * entry ("1,2" or "1.") survives until blur, when it resyncs to the canonical
 * number. `0` renders as an empty field with a placeholder so there is no stuck
 * "0" to type around.
 */
export interface MoneyInputProps
  extends Omit<
    React.ComponentProps<typeof Input>,
    "value" | "onChange" | "type" | "inputMode"
  > {
  value: number;
  onChange: (value: number) => void;
}

const GROUPED = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

/** Reduce arbitrary input to digits + at most one dot + at most two decimals. */
function sanitize(raw: string): string {
  const cleaned = raw.replace(/[^\d.]/g, "");
  const dot = cleaned.indexOf(".");
  if (dot === -1) return cleaned;
  const whole = cleaned.slice(0, dot);
  const decimals = cleaned.slice(dot + 1).replace(/\./g, "").slice(0, 2);
  return `${whole}.${decimals}`;
}

const addGroups = (whole: string) =>
  whole.replace(/^0+(?=\d)/, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/** "0387900.5" -> "387,900.5". A trailing dot the user just typed is kept. */
function format(raw: string): string {
  const clean = sanitize(raw);
  const dot = clean.indexOf(".");
  if (dot === -1) return addGroups(clean);
  return `${addGroups(clean.slice(0, dot))}.${clean.slice(dot + 1)}`;
}

const toNumber = (raw: string) => Number(sanitize(raw)) || 0;

const toText = (value: number) => (value === 0 ? "" : GROUPED.format(value));

/** Caret measured in significant chars, so separators don't shift it. */
const countSignificant = (text: string, upTo: number) =>
  text.slice(0, upTo).replace(/[^\d.]/g, "").length;

function caretAfter(text: string, significant: number): number {
  if (significant <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < text.length; i += 1) {
    if (/[\d.]/.test(text[i])) {
      seen += 1;
      if (seen === significant) return i + 1;
    }
  }
  return text.length;
}

export const MoneyInput = React.forwardRef<HTMLInputElement, MoneyInputProps>(
  (
    { value, onChange, className, placeholder = "0", onFocus, onBlur, ...props },
    ref,
  ) => {
    const [text, setText] = React.useState(() => toText(value));
    const [focused, setFocused] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const caret = React.useRef<number | null>(null);

    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    // Reflect external changes only while the user isn't editing.
    React.useEffect(() => {
      if (!focused) setText(toText(value));
    }, [value, focused]);

    // A newly inserted separator pushes every digit to its right, so the caret
    // has to be re-placed after React writes the formatted value to the DOM.
    React.useLayoutEffect(() => {
      const el = inputRef.current;
      if (caret.current === null || !el) return;
      el.setSelectionRange(caret.current, caret.current);
      caret.current = null;
    });

    return (
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">
          $
        </span>
        <Input
          {...props}
          ref={inputRef}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={text}
          placeholder={placeholder}
          className={cn("pl-7 tabular-nums", className)}
          onChange={(e) => {
            const raw = e.target.value;
            const next = format(raw);
            caret.current = caretAfter(
              next,
              countSignificant(raw, e.target.selectionStart ?? raw.length),
            );
            setText(next);
            onChange(toNumber(next));
          }}
          onFocus={(e) => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
        />
      </div>
    );
  },
);
MoneyInput.displayName = "MoneyInput";
